import Vue from 'vue';
import i18n from '@/i18n';
import { esgotado, saldoDisponivel } from '@/utils/estoque';

export const limiteMaxEtapa = passo => +passo?.max || 20;

export const escolhaUnica = passo => limiteMaxEtapa(passo) === 1;

export const escolhidosNaEtapa = passo =>
    (passo?.complementos || []).reduce((soma, c) => soma + (+c.quantidade || 0), 0);

export const escolhidosDaEtapa = passo =>
    (passo?.complementos || []).filter(c => +c.quantidade > 0);

export const etapaCheia = passo => escolhidosNaEtapa(passo) >= limiteMaxEtapa(passo);

export const isIndisponivel = complemento =>
    complemento?.status === 'inativo' || esgotado(complemento);

export const podeSomar = (passo, complemento) => {
    if (isIndisponivel(complemento)) return false;

    const atual = +complemento.quantidade || 0;
    const maxDoItem = +complemento.max || limiteMaxEtapa(passo);

    if (atual >= saldoDisponivel(complemento)) return false;
    if (atual >= maxDoItem) return false;
    if (etapaCheia(passo)) return false;

    return true;
};

export const podeTocar = (passo, complemento) => {
    if (isIndisponivel(complemento)) return false;
    if (escolhaUnica(passo)) return true;

    return podeSomar(passo, complemento);
};

export const tocar = (passo, complemento) => {
    if (!podeTocar(passo, complemento)) return false;

    if (escolhaUnica(passo)) {
        (passo.complementos || []).forEach(c => Vue.set(c, 'quantidade', c === complemento ? 1 : 0));
        return true;
    }

    return somar(passo, complemento);
};

export const somar = (passo, complemento) => {
    if (!podeSomar(passo, complemento)) return false;

    Vue.set(complemento, 'quantidade', (+complemento.quantidade || 0) + 1);
    return true;
};

export const remover = complemento => {
    const atual = +complemento.quantidade || 0;
    if (atual <= 0) return false;

    Vue.set(complemento, 'quantidade', atual - 1);
    return true;
};

export const qtdMaxDoComplemento = (passo, complemento) =>
    +complemento?.max || limiteMaxEtapa(passo);

export const definirQuantidade = (passo, complemento, valor) => {
    const atual = +complemento.quantidade || 0;
    let nova = Math.max(0, Math.floor(+valor || 0));

    if (nova === atual) {
        return { quantidade: atual, aviso: null };
    }

    const sobraDaEtapa = limiteMaxEtapa(passo) - (escolhidosNaEtapa(passo) - atual);
    const teto = Math.max(0, Math.min(qtdMaxDoComplemento(passo, complemento), sobraDaEtapa));
    const tetoEstoque = saldoDisponivel(complemento);

    let aviso = null;

    if (nova > Math.min(teto, tetoEstoque)) {
        nova = Math.min(teto, tetoEstoque);

        aviso = tetoEstoque < teto
            ? i18n.t('etapa.estoqueDisponivel', { n: nova })
            : i18n.t('etapa.quantidadeMaxima', { n: nova });
    }

    Vue.set(complemento, 'quantidade', nova);

    return { quantidade: nova, aviso };
};

export const pendenciaDaEtapa = passo => {
    const minimo = +passo?.min || 0;
    const escolhidos = escolhidosNaEtapa(passo);

    if (minimo && escolhidos < minimo) {
        return minimo === 1 ? i18n.t('etapa.escolhaUma') : i18n.t('etapa.escolhaAoMenos', { n: minimo });
    }

    const abaixoDoMinimo = (passo?.complementos || [])
        .find(c => +c.quantidade > 0 && +c.quantidade < +c.min);

    if (abaixoDoMinimo) {
        return i18n.t('etapa.minimoDe', { n: abaixoDoMinimo.min, item: abaixoDoMinimo.descricao });
    }

    return null;
};

export const instrucaoDaEtapa = passo => {
    const min = +passo?.min || 0;
    const max = limiteMaxEtapa(passo);
    const n = escolhidosNaEtapa(passo);
    const escolhidos = n ? i18n.t(n === 1 ? 'etapa.escolhidoUm' : 'etapa.escolhidos', { n }) : '';

    if (max === 1) return min ? i18n.t('etapa.escolha1Opcao') : i18n.t('etapa.escolha1SeQuiser');
    if (min && min === max) return i18n.t('etapa.escolhaN', { n: min, escolhidos });
    if (min) return i18n.t('etapa.escolhaDeAte', { min, max, escolhidos });

    return i18n.t('etapa.ateOpcoes', { max, escolhidos });
};

export const seloDaEtapa = passo => {
    if (!+passo?.min) return { texto: i18n.t('etapa.opcional'), classe: 'selo--opcional' };

    return pendenciaDaEtapa(passo)
        ? { texto: i18n.t('etapa.obrigatorio'), classe: 'selo--obrigatorio' }
        : { texto: i18n.t('etapa.pronto'), classe: 'selo--ok' };
};

export const etapasDoProduto = produto => {
    if (!produto) return [];

    if (produto.produtos?.length) {
        const lista = [];

        produto.produtos.forEach(sub => {
            (sub.passos || []).forEach(passo => lista.push({ passo, contexto: sub.descricao }));
        });

        return lista;
    }

    return (produto.passos || []).map(passo => ({ passo, contexto: null }));
};

export const limparEscolhasVazias = produto => {
    const limpar = alvo => {
        if (!alvo?.passos?.length) return;

        alvo.passos.forEach(passo => {
            passo.complementos = (passo.complementos || []).filter(c => +c.quantidade > 0);
        });

        alvo.passos = alvo.passos.filter(passo => passo.complementos.length);
    };

    if (produto?.produtos?.length) {
        produto.produtos.forEach(limpar);
        return produto;
    }

    limpar(produto);
    return produto;
};

export const zerarEscolhas = produto => {
    const zerar = passos => (passos || []).forEach(passo => {
        (passo.complementos || []).forEach(c => Vue.set(c, 'quantidade', 0));
    });

    zerar(produto?.passos);
    (produto?.produtos || []).forEach(sub => zerar(sub.passos));

    return produto;
};
